import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  FiClock, FiCalendar, FiVideo, FiCheck, FiAlertCircle,
  FiChevronLeft, FiChevronRight, FiUser, FiMail, FiMessageSquare, FiArrowLeft, FiGlobe
} from "react-icons/fi";
import {
  getPublicMeetingType,
  getPublicSlots,
  getPublicMonthAvailability,
  createPublicBooking
} from "../../api/meetingsApi";

const toDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const DAYS_SHORT = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

export default function BookingPage() {
  const { slug } = useParams();

  const [step, setStep] = useState("schedule"); // 'schedule' | 'form' | 'confirm'
  const [meetingType, setMeetingType] = useState(null);
  const [loadingType, setLoadingType] = useState(true);
  const [typeError, setTypeError] = useState(null);

  // Month navigation
  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });

  // Client timezone detection & current time
  const [clientTz] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  });

  const [clientTimeStr, setClientTimeStr] = useState("");

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: clientTz,
      });
      setClientTimeStr(timeStr);
    };
    updateTime();
    const interval = setInterval(updateTime, 30000);
    return () => clearInterval(interval);
  }, [clientTz]);

  // Availability & slots state
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Booking form state
  const [form, setForm] = useState({ client_name: "", client_email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);

  // Load meeting type on mount
  useEffect(() => {
    getPublicMeetingType(slug)
      .then(data => {
        if (data.error) throw new Error(data.error);
        setMeetingType(data);
      })
      .catch(e => setTypeError(e.message || "Meeting not found"))
      .finally(() => setLoadingType(false));
  }, [slug]);

  // Load month availability when viewDate or slug changes
  const fetchMonthAvailability = useCallback(async () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth() + 1;
    setLoadingMonth(true);
    try {
      const data = await getPublicMonthAvailability(slug, year, month);
      const dates = data.available_dates || [];
      setAvailableDates(dates);

      // Auto-select the first available date if none selected
      if (dates.length > 0) {
        setSelectedDate(dates[0]);
      } else {
        setSelectedDate(null);
      }
    } catch {
      setAvailableDates([]);
      setSelectedDate(null);
    } finally {
      setLoadingMonth(false);
    }
  }, [slug, viewDate]);

  useEffect(() => {
    if (!loadingType && !typeError) {
      fetchMonthAvailability();
    }
  }, [loadingType, typeError, fetchMonthAvailability]);

  // Load slots when selectedDate changes
  useEffect(() => {
    if (!selectedDate) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    setSelectedSlot(null);
    getPublicSlots(slug, selectedDate)
      .then(data => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [slug, selectedDate]);

  const handleDateClick = (dateStr) => {
    setSelectedDate(dateStr);
  };

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
    setStep("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;
    setSubmitting(true);
    try {
      const data = await createPublicBooking(slug, {
        ...form,
        scheduled_at: selectedSlot.datetime_utc,
      });
      if (data.error) throw new Error(data.error);
      setBooking(data);
      setStep("confirm");
    } catch (e) {
      alert(e.message || "Failed to book meeting. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatHeaderDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  };

  // Calendar matrix calculations
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDayIndex = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const todayStr = toDateStr(new Date());

  const calendarCells = [];
  for (let i = 0; i < firstDayIndex; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(new Date(year, month, d));

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  // Loading Screen
  if (loadingType) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="w-8 h-8 border-3 border-[#0069ff] border-r-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Error Screen
  if (typeError) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8 text-center max-w-md w-full">
          <FiAlertCircle size={44} className="mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Page Not Found</h1>
          <p className="text-slate-500 text-sm">{typeError}</p>
        </div>
      </div>
    );
  }

  // Confirmation Screen
  if (step === "confirm" && booking) {
    const dt = new Date(booking.scheduled_at);
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 sm:p-6 font-sans">
        <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 max-w-lg w-full overflow-hidden">
          <div className="bg-[#0069ff] p-8 text-center text-white">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
              <FiCheck size={32} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold">You are scheduled!</h1>
            <p className="text-blue-100 text-sm mt-1">A calendar invitation has been sent to your email.</p>
          </div>

          <div className="p-8 space-y-6">
            <div className="bg-slate-50 rounded-2xl p-6 space-y-4 border border-slate-100">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-100 text-[#0069ff] flex items-center justify-center shrink-0">
                  <FiCalendar size={20} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Meeting Topic</p>
                  <p className="text-base font-bold text-slate-800">{booking.meeting_title}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                  <FiClock size={20} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Date & Time</p>
                  <p className="text-base font-bold text-slate-800">
                    {dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                  </p>
                  <p className="text-sm font-semibold text-[#0069ff] mt-0.5">
                    {dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })} ({clientTz})
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                  <FiUser size={20} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Host</p>
                  <p className="text-base font-bold text-slate-800">{meetingType.host_name}</p>
                </div>
              </div>
            </div>

            {booking.google_meet_link && (
              <a
                href={booking.google_meet_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-4 bg-[#0069ff] hover:bg-[#0052cc] text-white rounded-2xl font-bold shadow-lg shadow-blue-500/20 transition-all text-sm"
              >
                <FiVideo size={18} /> Join Google Meet
              </a>
            )}

            <div className="text-center pt-2">
              <span className="text-xs text-slate-400 font-medium">Powered by ihamza.online</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 sm:p-8 font-sans">
      {/* Calendly Card Container */}
      <div className="bg-white rounded-3xl shadow-xl border border-slate-200/80 w-full max-w-4xl overflow-hidden flex flex-col md:flex-row">

        {/* LEFT PANEL: Host & Meeting Details */}
        <div className="w-full md:w-80 p-8 bg-white border-b md:border-b-0 md:border-r border-slate-200/80 flex flex-col justify-between shrink-0">
          <div>
            {/* Host Avatar & Info */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#0069ff] text-white flex items-center justify-center font-bold text-xl shadow-md shrink-0">
                {meetingType.host_name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{meetingType.host_name}</p>
                <h1 className="text-slate-900 font-bold text-lg leading-snug truncate">{meetingType.title}</h1>
              </div>
            </div>

            {/* Badges */}
            <div className="space-y-3.5 mb-6">
              <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                <FiClock size={18} className="text-slate-400 shrink-0" />
                <span>{meetingType.duration_mins} mins</span>
              </div>

              {meetingType.calendar_connected && (
                <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                  <FiVideo size={18} className="text-slate-400 shrink-0" />
                  <span>Google Meet</span>
                </div>
              )}
            </div>

            {/* Description */}
            {meetingType.description && (
              <p className="text-xs text-slate-500 leading-relaxed mb-6">
                {meetingType.description}
              </p>
            )}

            {/* Form step selected summary */}
            {step === "form" && selectedDate && selectedSlot && (
              <div className="p-4 bg-blue-50/80 border border-blue-200/60 rounded-2xl text-xs space-y-1">
                <p className="font-semibold text-blue-900">Selected Slot:</p>
                <p className="font-bold text-[#0069ff]">{formatHeaderDate(selectedDate)}</p>
                <p className="font-extrabold text-blue-900 text-sm">{selectedSlot.display}</p>
              </div>
            )}
          </div>

          <div className="pt-6 border-t border-slate-100">
            <span className="text-xs text-slate-400 font-medium">Powered by ihamza.online</span>
          </div>
        </div>

        {/* RIGHT PANEL: Exact Calendly Date & Time Picker */}
        <div className="flex-1 p-6 sm:p-8 flex flex-col justify-between min-w-0">

          {/* STEP 1: Date & Time Picker */}
          {step === "schedule" && (
            <div className="flex flex-col h-full justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900 mb-6">Select a Date & Time</h2>

                <div className="flex flex-col lg:flex-row gap-8 items-start">

                  {/* CALENDAR COLUMN */}
                  <div className="flex-1 w-full min-w-0">
                    {/* Month Nav */}
                    <div className="flex items-center justify-between mb-6 px-1">
                      <span className="text-sm font-bold text-slate-800">
                        {MONTHS[month]} {year}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={prevMonth}
                          className="p-2 rounded-full hover:bg-slate-100 text-[#0069ff] transition"
                        >
                          <FiChevronLeft size={18} />
                        </button>
                        <button
                          onClick={nextMonth}
                          className="p-2 rounded-full hover:bg-slate-100 text-[#0069ff] transition"
                        >
                          <FiChevronRight size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Day Headers */}
                    <div className="grid grid-cols-7 mb-3 text-center">
                      {DAYS_SHORT.map(d => (
                        <span key={d} className="text-[10px] font-bold text-slate-400 tracking-wider py-1">
                          {d}
                        </span>
                      ))}
                    </div>

                    {/* Calendar Grid (Properly Sized Circles with Zero Horizontal Overlap) */}
                    <div className="grid grid-cols-7 gap-y-2.5 gap-x-1 justify-items-center relative">
                      {loadingMonth && (
                        <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-2xl">
                          <div className="w-6 h-6 border-3 border-[#0069ff] border-r-transparent rounded-full animate-spin" />
                        </div>
                      )}
                      {calendarCells.map((date, idx) => {
                        if (!date) return <div key={`empty-${idx}`} className="w-8 h-8" />;
                        const dateStr = toDateStr(date);
                        const isAvailable = availableDates.includes(dateStr);
                        const isSelected = selectedDate === dateStr;
                        const isToday = dateStr === todayStr;

                        return (
                          <div key={dateStr} className="flex flex-col items-center justify-center">
                            <button
                              disabled={!isAvailable}
                              onClick={() => handleDateClick(dateStr)}
                              className={`
                                w-8 h-8 sm:w-9 sm:h-9 rounded-full text-xs font-bold transition-all flex items-center justify-center shrink-0
                                ${isAvailable
                                  ? isSelected
                                    ? "bg-[#0069ff] text-white shadow-md shadow-blue-500/30 font-extrabold scale-105"
                                    : "bg-[#e8f0fe] text-[#0069ff] hover:bg-[#0069ff] hover:text-white cursor-pointer font-extrabold"
                                  : "text-slate-400 hover:bg-transparent cursor-not-allowed font-medium"
                                }
                              `}
                            >
                              {date.getDate()}
                            </button>
                            {/* Today Dot Indicator */}
                            {isToday && !isSelected && (
                              <span className="w-1 h-1 bg-slate-800 rounded-full mt-0.5" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* TIME SLOTS COLUMN */}
                  {selectedDate && (
                    <div className="w-full lg:w-56 pt-6 lg:pt-0 border-t lg:border-t-0 lg:border-l border-slate-200/80 lg:pl-6 flex flex-col shrink-0">
                      <p className="text-sm font-bold text-slate-800 mb-5">
                        {formatHeaderDate(selectedDate)}
                      </p>

                      {loadingSlots ? (
                        <div className="flex items-center justify-center py-10">
                          <div className="w-6 h-6 border-3 border-[#0069ff] border-r-transparent rounded-full animate-spin" />
                        </div>
                      ) : slots.length === 0 ? (
                        <div className="p-4 bg-slate-50 rounded-2xl text-center text-xs text-slate-400 italic">
                          No available slots for this date.
                        </div>
                      ) : (
                        <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
                          {slots.map(slot => (
                            <button
                              key={slot.datetime}
                              onClick={() => handleSlotClick(slot)}
                              className="w-full py-3 px-4 border-2 border-[#0069ff] text-[#0069ff] font-bold bg-white rounded-xl text-sm hover:bg-[#0069ff] hover:text-white transition-all text-center shadow-sm"
                            >
                              {slot.display}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>

              {/* Timezone Selector at Bottom Left */}
              <div className="mt-8 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-semibold text-slate-700">
                <FiGlobe size={15} className="text-slate-500 shrink-0" />
                <span>
                  {clientTz.replace(/_/g, " ")} ({clientTimeStr})
                </span>
              </div>
            </div>
          )}

          {/* STEP 2: Client Details Form */}
          {step === "form" && (
            <div className="flex flex-col h-full justify-between">
              <div>
                <button
                  onClick={() => setStep("schedule")}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-[#0069ff] transition mb-6 w-fit"
                >
                  <FiArrowLeft size={16} /> Back to Date & Time
                </button>

                <h2 className="text-lg font-bold text-slate-900 mb-6">Enter Your Details</h2>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Your Full Name *</label>
                    <div className="relative">
                      <FiUser size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        required
                        value={form.client_name}
                        onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))}
                        placeholder="John Smith"
                        className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0069ff] font-medium transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Your Email Address *</label>
                    <div className="relative">
                      <FiMail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="email"
                        required
                        value={form.client_email}
                        onChange={e => setForm(p => ({ ...p, client_email: e.target.value }))}
                        placeholder="john@example.com"
                        className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0069ff] font-medium transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Additional Notes</label>
                    <div className="relative">
                      <FiMessageSquare size={16} className="absolute left-3.5 top-3.5 text-slate-400" />
                      <textarea
                        value={form.notes}
                        onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                        placeholder="Please share anything that will help prepare for our meeting"
                        rows={3}
                        className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0069ff] font-medium transition resize-none"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 bg-[#0069ff] hover:bg-[#0052cc] text-white rounded-2xl font-bold shadow-lg shadow-blue-500/20 transition-all text-sm flex items-center justify-center gap-2 mt-6"
                  >
                    {submitting ? (
                      <><span className="w-4 h-4 border-2 border-white border-r-transparent rounded-full animate-spin" /> Confirming Booking...</>
                    ) : (
                      "Schedule Meeting"
                    )}
                  </button>
                </form>
              </div>

              <div className="mt-8 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-semibold text-slate-700">
                <FiGlobe size={15} className="text-slate-500 shrink-0" />
                <span>
                  {clientTz.replace(/_/g, " ")} ({clientTimeStr})
                </span>
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
}
